(() => {
  if (customElements.get('pickup-availability')) return;
  let nextId = 0;

  class PickupAvailability extends HTMLElement {
    generation = 0;

    connectedCallback() {
      if (this.events || !this.isConnected) return;
      this.errorHtml ||= this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
      if (!this.errorHtml) return;
      this.events = new AbortController();
      this.drawerId ||= `PickupAvailability-${++nextId}`;
      this.canFetch ??= this.hasAttribute('available');
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (this.drawer) this.drawer.show(button);
        else this.fetchAvailability(this.dataset.variantId);
      }, { signal: this.events.signal });
      if (this.canFetch) this.fetchAvailability(this.dataset.variantId);
    }

    disconnectedCallback() {
      this.cancelRequest();
      this.events?.abort();
      this.events = null;
      this.removeDrawer();
      this.removeAttribute('aria-busy');
    }

    cancelRequest() {
      this.generation++;
      this.request?.abort();
      this.request = null;
    }

    removeDrawer() {
      this.drawer?.hide(false);
      this.drawer?.remove();
      this.drawer = null;
    }

    async fetchAvailability(variantId) {
      if (!variantId || !this.events || !this.isConnected) return;
      const id = String(variantId);
      if (this.request && this.requestId === id) return;
      this.cancelRequest();
      const generation = this.generation;
      this.request = new AbortController();
      this.requestId = id;
      this.dataset.variantId = id;
      this.canFetch = true;
      this.removeDrawer();
      this.replaceChildren();
      this.removeAttribute('available');
      this.setAttribute('aria-busy', 'true');
      const rootUrl = (this.dataset.rootUrl || '/').replace(/\/?$/, '/');
      try {
        const response = await fetch(`${rootUrl}variants/${id}/?section_id=pickup-availability`, { signal: this.request.signal });
        if (!response.ok) throw new Error('Pickup availability request failed');
        const text = await response.text();
        if (generation !== this.generation || !this.isConnected) return;
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section');
        if (!section) throw new Error('Invalid pickup availability response');
        this.renderPreview(section);
      } catch (error) {
        if (generation === this.generation && this.isConnected && error.name !== 'AbortError') this.renderError();
      } finally {
        if (generation === this.generation) {
          this.request = null;
          this.removeAttribute('aria-busy');
        }
      }
    }

    update(variant) {
      this.dataset.variantId = String(variant?.id || '');
      this.canFetch = Boolean(variant?.available);
      if (this.canFetch) this.fetchAvailability(variant.id);
      else {
        this.cancelRequest();
        this.removeDrawer();
        this.replaceChildren();
        this.removeAttribute('available');
        this.removeAttribute('aria-busy');
      }
    }

    renderError() {
      this.replaceChildren(this.errorHtml.cloneNode(true));
      this.setAttribute('available', '');
    }

    renderPreview(section) {
      const preview = section.querySelector('pickup-availability-preview');
      if (!preview) return;
      const drawer = section.querySelector('pickup-availability-drawer');
      if (!drawer) throw new Error('Missing pickup availability drawer');
      drawer.id = this.drawerId;
      const heading = drawer.querySelector('[id="PickupAvailabilityHeading"]');
      if (heading) {
        heading.id = `${this.drawerId}-Heading`;
        drawer.setAttribute('aria-labelledby', heading.id);
      }
      const button = preview.querySelector('button');
      if (button) {
        button.id = `${this.drawerId}-Toggle`;
        button.type = 'button';
        button.setAttribute('aria-controls', drawer.id);
        button.setAttribute('aria-expanded', 'false');
      }
      (this.dataset.productPageColorScheme || '').split(' ').filter(Boolean).forEach((name) => drawer.classList.add(name));
      this.replaceChildren(preview);
      this.drawer = drawer;
      document.body.append(drawer);
      this.setAttribute('available', '');
    }
  }

  class PickupAvailabilityDrawer extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const { signal } = this.events;
      this.inert = true;
      this.setAttribute('aria-hidden', 'true');
      this.querySelector('button')?.addEventListener('click', () => this.hide(), { signal });
      document.addEventListener('ac:overlay-open', () => this.hide(false), { signal });
      this.addEventListener('keyup', (event) => {
        if (!this.layerManager && event.key === 'Escape') this.hide();
      }, { signal });
      this.onBodyClick = (event) => {
        if (!this.contains(event.target) && !this.focusElement?.contains(event.target)) this.hide(false);
      };
    }

    disconnectedCallback() {
      this.hide(false);
      this.events?.abort();
      this.events = null;
    }

    show(focusElement) {
      if (this.hasAttribute('open')) return;
      this.focusElement = focusElement;
      this.inert = false;
      this.setAttribute('aria-hidden', 'false');
      this.setAttribute('open', '');
      focusElement?.setAttribute('aria-expanded', 'true');
      this.layerManager = window.AcLayerManager;
      if (this.layerManager) {
        this.layerManager.acquire(this.id, { opener: focusElement, getFocusRoots: () => [this], onEscape: () => this.hide() });
        this.focus({ preventScroll: true });
      } else {
        document.body.classList.add('overflow-hidden');
        trapFocus(this);
      }
      // Acquire the new owner before retiring another pickup drawer's lock.
      document.querySelectorAll('pickup-availability-drawer[open]').forEach((drawer) => {
        if (drawer !== this) drawer.hide(false);
      });
      document.body.addEventListener('click', this.onBodyClick, { signal: this.events.signal });
    }

    hide(restoreFocus = true) {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.inert = true;
      this.setAttribute('aria-hidden', 'true');
      this.focusElement?.setAttribute('aria-expanded', 'false');
      document.body.removeEventListener('click', this.onBodyClick);
      if (this.layerManager) this.layerManager.release(this.id, { restoreFocus });
      else {
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(restoreFocus ? this.focusElement : null);
      }
      this.layerManager = null;
      this.focusElement = null;
    }
  }

  customElements.define('pickup-availability', PickupAvailability);
  if (!customElements.get('pickup-availability-drawer')) customElements.define('pickup-availability-drawer', PickupAvailabilityDrawer);
  const visit = (scope, method) => {
    if (scope.matches?.('pickup-availability')) scope[method]();
    scope.querySelectorAll?.('pickup-availability').forEach((element) => element[method]());
  };
  document.addEventListener('shopify:section:load', (event) => visit(event.target, 'connectedCallback'));
  document.addEventListener('shopify:section:unload', (event) => visit(event.target, 'disconnectedCallback'));
})();
